import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getMemberPhoto, getMemberPhotoRequirements, loadLoyaltyMasterData, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { isToday } from 'date-fns';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useMemberContext } from '../../../context/member/hooks/use-member-context';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { Button } from '../../../subcomponents/button/button';
import { ButtonColor } from '../../../subcomponents/button/types/color';
import { ButtonSize } from '../../../subcomponents/button/types/size';
import { DisplayText } from '../../../subcomponents/display-text/display-text';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { MemberPhotoUploaderMessage, MemberPhotoUploaderMessageType } from './subcomponents/message';
import { MemberPhotoUploaderPhoto } from './subcomponents/photo';
import { MemberPhotoUploaderPrompt } from './subcomponents/prompt';
import { MemberPhotoUploaderSkeleton } from './subcomponents/skeleton';
import { mapMemberPhotoUploaderContext, MemberPhotoUploaderContextInstance } from './types/context';
import { defaultMemberPhotoUploaderProps, memberPhotoUploaderPropsValidator } from './types/props';
const b = bemClassBuilder('v-member-photo-uploader');
export const MemberPhotoUploader = observer(props => {
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { displayText, assets } = props;
    const [recentlyUploaded, setRecentlyUploaded] = useState(false);
    const [changingPhoto, setChangingPhoto] = useState(false);
    const [failedToLoad, setFailedToLoad] = useState(false);
    const { member } = useMemberContext();
    useEffect(() => {
        (async () => {
            try {
                await loadLoyaltyMasterData();
            }
            catch (_a) {
                logger.error('Could not load member management data');
                setFailedToLoad(true);
            }
        })();
    }, []);
    const handlePhotoUploaded = () => {
        setRecentlyUploaded(true);
        setChangingPhoto(false);
    };
    const handleCancelUpdateButtonClicked = () => {
        setChangingPhoto(false);
    };
    const existingPhoto = member === null || member === void 0 ? void 0 : member.photo;
    const helpTextLabel = getHelpTextLabel(displayText, recentlyUploaded);
    if (failedToLoad) {
        return (_jsx(MemberPhotoUploaderMessage, { messageType: MemberPhotoUploaderMessageType.FailedToLoadData, assets: assets, displayText: displayText }));
    }
    const photoRequirements = getMemberPhotoRequirements();
    if (!member || !photoRequirements) {
        return enableSkeletonLoadingIndicators ? _jsx(MemberPhotoUploaderSkeleton, {}) : null;
    }
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsxs(MemberPhotoUploaderContextInstance.Provider, Object.assign({ value: mapMemberPhotoUploaderContext(props) }, { children: [_jsx(DisplayText, { className: b.element('help-text'), label: helpTextLabel, templateParameters: {
                        minimumDaysBetweenUpdates: photoRequirements.minimumDaysBetweenUpdates,
                        canBeUpdatedAt: existingPhoto === null || existingPhoto === void 0 ? void 0 : existingPhoto.canBeUpdatedAt
                    } }), !!existingPhoto && !changingPhoto ? (_jsx(MemberPhotoUploaderPhoto, { member: member, onUpdatePhotoClick: () => setChangingPhoto(true) })) : (_jsx(MemberPhotoUploaderPrompt, { onPhotoUploaded: handlePhotoUploaded, photoRequirements: photoRequirements })), changingPhoto && (_jsx(Button, { className: b.element('cancel-update-button'), color: ButtonColor.Tertiary, size: ButtonSize.Small, label: displayText.cancelUpdatePhotoButtonLabel, onClick: handleCancelUpdateButtonClicked }))] })) })));
});
MemberPhotoUploader.displayName = 'MemberPhotoUploader';
/**
 * The **Member Photo Uploader** component allows a Loyalty member to upload or replace their photo.
 */
export const MemberPhotoUploaderComponent = withComponentWrapper(MemberPhotoUploader, defaultMemberPhotoUploaderProps, memberPhotoUploaderPropsValidator);
const getHelpTextLabel = (displayText, recentlyUploaded) => {
    if (recentlyUploaded)
        return displayText.helpTextSuccessfullyUploadedMessage;
    const existingPhoto = getMemberPhoto();
    if (!existingPhoto)
        return displayText.helpTextNoExistingPhotoMessage;
    if (existingPhoto.isUpdatable)
        return displayText.helpTextExistingPhotoUnlockedMessage;
    return isToday(new Date(existingPhoto.canBeUpdatedAt))
        ? displayText.helpTextExistingPhotoLockedTodayMessage
        : displayText.helpTextExistingPhotoLockedMessage;
};
